import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {DataService} from './data.service';
import {RestService} from './rest.service';
import {SessionStorageService} from './session-storage.service';

@Injectable({providedIn: 'root'})
export class SessionGuard implements CanActivate {
    constructor(private dataService: DataService,
                private restService: RestService,
                private sessionStorage: SessionStorageService,
                private router: Router) {
    }

    canActivate(_route: ActivatedRouteSnapshot,
                state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        if (this.dataService.getSessionIdentifier()) return true;

        const loginUrl = this.router.createUrlTree(['/login'], {
            queryParams: {returnUrl: state.url}
        });

        return this.sessionStorage.getItem<string>('SessionIdentifier').pipe(
            switchMap(sessionIdentifier => {
                if (!sessionIdentifier) return of(loginUrl);

                return this.restService.checkSession(sessionIdentifier).pipe(
                    switchMap(response => {
                        if (this.dataService.isResponseSuccessful(response) && response.UserInfo) {
                            this.dataService.setUserInfo(response.UserInfo);
                            return of(true);
                        }

                        return this.clearSessionAndRedirect(loginUrl);
                    }),
                    catchError(() => this.clearSessionAndRedirect(loginUrl))
                );
            }),
            catchError(() => of(loginUrl))
        );
    }

    private clearSessionAndRedirect(loginUrl: UrlTree): Observable<UrlTree> {
        this.dataService.clearSession();
        return this.sessionStorage.clear().pipe(
            map(() => loginUrl),
            catchError(() => of(loginUrl))
        );
    }
}
